import { forwardRef, useEffect, useRef, useState } from 'react'

import './_comment.scss'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'

import axiosInstance from 'api/axiosInstance'
import avatarPlaceholder from 'assets/images/user_placeholder.png'
import {
    IconHeartFill,
    IconLikeOutline
} from 'assets/svg/sprite'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import Report from 'components/UI/Modal/Report/Report'
import UserDetails from 'components/UI/Modal/UserDetails/UserDetails'
import { useModalContext } from 'context/modalContext'
import { AllIcons } from 'helpers/allIcons'
import { useOutsideAlerter, useReportComment } from 'helpers/hooks'
import { useLongPress } from 'helpers/hooks/useLongPress'
import { someTimeAgo } from 'lib/dayjs'
import { useUserProfile } from 'store'

interface ICommentEntity {
    entityId: string
    parentId: string
    owner?: string
    ownerId: string
    isReply: boolean
}

interface Props {
    data: any
    isReply: boolean
    repliesToggled?: boolean
    commentSelected: boolean
    selectedEntity: ICommentEntity | null
    isCommentReply: boolean
    isMyPost: boolean
    refetchComments: () => void
    toggleReplies?: () => void
    setIsCommentReply: (value: boolean) => void
    setSelectedEntity: (commentEntity: ICommentEntity | null) => void
    setInitialCommentLiked: (commentId: string) => void
    deleteComment: () => void
    optionsActive: boolean
    setOptionsActive: React.Dispatch<React.SetStateAction<boolean>>
    startReply: (params: {
        entityId: string
        parentId: string
        ownerId: string
        owner?: string
        isReply: boolean
    }) => void
}

const Comment = forwardRef<HTMLInputElement, Props>(
    (
        {
            data,
            refetchComments,
            isReply,
            repliesToggled,
            commentSelected,
            selectedEntity,
            isCommentReply,
            isMyPost,
            toggleReplies,
            setIsCommentReply,
            setSelectedEntity,
            setInitialCommentLiked,
            deleteComment,
            optionsActive,
            setOptionsActive,
            startReply
        },
        ref
    ) => {
        const [showOptions, setShowOptions] = useState(false)
        const modalData = useModalContext()
        const user = useUserProfile()
        const { t } = useTranslation()
        const { reportComment } = useReportComment()
        const isMyComment = data.user_id === user?.id
        const canReport = !isMyComment
        const canDelete = isMyComment || isMyPost
        const optionsRef = useRef<HTMLDivElement | null>(null)


        useOutsideAlerter(
            optionsRef,
            () => {
                setOptionsActive(false)
                setShowOptions(false)
            },
            ['comment__options', 'comment__options__option']
        )

        const [isLiked, setIsLiked] = useState<boolean>(data.is_liked)
        const [likeCount, setLikeCount] = useState<number>(data.like_count)

        const likeComment = useMutation(
            async (liked: boolean) => {
                if (liked) {
                    await axiosInstance.delete(`/api/like/comment/${data.id}`)
                } else {
                    await axiosInstance.post(`/api/like/comment/${data.id}`)
                }
            },
            {
                onMutate: (liked: boolean) => {
                    setIsLiked(!liked)
                    setLikeCount((prev: number) =>
                        Math.max(0, liked ? prev - 1 : prev + 1)
                    )
                },
                onError: () => {
                    setIsLiked(data.is_liked)
                    setLikeCount(data.like_count)
                },
                onSuccess: () => {
                    refetchComments()
                }
            }
        )

        const handleCommentDelete = () => {
            modalData.addModal(
                t('deleteComment'),
                <ConfirmModal
                    body={t('onceYouDeleteThisComment')}
                    confirmFn={() => {
                        setOptionsActive(false)
                        setShowOptions(false)
                        deleteComment()
                    }}
                />
            )
        }

        const handleCommentReport = () => {
            modalData.addModal(
                t('reportComment'),
                <Report
                    type='content'
                    confirmFn={(message: string, reportType: number) => {
                        reportComment.mutate(
                            { commentId: data.id, message, reportType },
                            {
                                onSuccess: () => {
                                    setOptionsActive(false)
                                    setShowOptions(false)
                                    modalData.clearModal()
                                }
                            }
                        )
                    }}
                    closeFn={() => {
                        modalData.clearModal()
                    }}
                />
            )
        }

        const replies = Array.isArray(data.comments) ? data.comments : []

        const replyOwner =
            data?.user?.username ||
            data?.user?.display_name ||
            data?.user?.first_name ||
            'user'

        const handleSelectComment = () => {
            const isSameSelected =
                isCommentReply &&
                selectedEntity?.entityId === data.id

            if (isSameSelected) {
                setIsCommentReply(false)
                setSelectedEntity(null)
                return
            }

            startReply({
                entityId: data.id,
                parentId: isReply ? data.entity_id : data.id,
                ownerId: data.user_id,
                owner: replyOwner,
                isReply
            })
        }

        const longPressHandlers = useLongPress({
            delay: 500,
            moveThreshold: 20,
            onLongPress: () => {
                setOptionsActive(true)
                setShowOptions(true)
            },
            onClick: () => {
                handleSelectComment()
            }
        })

        return (
            <>
                <div
                    className={`comment ${isReply ? 'comment__reply' : ''}`}
                    onClick={() => {
                        setOptionsActive(false)
                        setShowOptions(false)
                    }}
                >
                    <div
                        className={`comment__avatar ${
                            repliesToggled ?? isReply
                                ? 'comment__avatar--withreplies'
                                : ''
                        }`}
                    >
                        {data?.user?.is_model ? (
                            <Link reloadDocument to={`/${data?.user?.display_name}`}>
                                <AvatarHolder
                                    img={data?.user?.avatar?.url || avatarPlaceholder}
                                    size='40'
                                />
                            </Link>
                        ) : (
                            <div
                                onClick={() =>
                                    modalData.addModal(
                                        '',
                                        <UserDetails user={data.user} />,
                                        true
                                    )
                                }
                            >
                                <AvatarHolder
                                    img={data?.user?.avatar?.url || avatarPlaceholder}
                                    size='40'
                                />
                            </div>
                        )}
                    </div>

                    <div className='comment__body'>
                        <div className='comment__user'>
                            <div className='comment__user--left'>
                                {data?.user?.is_model ? (
                                    <Link
                                        reloadDocument
                                        to={`/${data?.user?.display_name}`}
                                        className='comment__user__name'
                                    >
                                        {data?.user?.display_name}
                                    </Link>
                                ) : (
                                    <span
                                        className='comment__user__name'
                                        onClick={() =>
                                            modalData.addModal(
                                                '',
                                                <UserDetails user={data.user} />,
                                                true
                                            )
                                        }
                                    >
                                        {data?.user?.display_name}
                                    </span>
                                )}

                                <span className='comment__user__time'>
                                    {someTimeAgo(data.created_at)}
                                </span>
                            </div>
                        </div>

<div className='comment__content'>
    <div className='comment__content__bubbleWrap'>
        <div
            className={`comment__content__text ${
                commentSelected
                    ? 'comment__content__text--selected'
                    : ''
            }`}
            onClick={(e) => {
                e.stopPropagation()
            }}
            {...longPressHandlers}
        >
            <p>{data.comment}</p>
        </div>

        {likeCount > 0 && (
            <div className='comment__reactions_container'>
                <div className='comment__reaction'>
                    <span className='comment__reaction__emoji'>❤️</span>
                    <span>{likeCount}</span>
                </div>
            </div>
        )}
    </div>

    {!isMyComment && (
        <div className='comment__content__actions'>
            <div
                className='comment__content__likeButton'
                onClick={(e) => {
                    e.stopPropagation()
                    likeComment.mutate(isLiked)
                }}
            >
                {isLiked ? <IconHeartFill /> : <IconLikeOutline />}
            </div>
        </div>
    )}
</div>

                        <div className='comment__actions'>
                            {!isReply && replies.length > 0 && (
                                <div
                                    className={`comment__actions__expand ${
                                        repliesToggled
                                            ? 'comment__actions__expand--active'
                                            : ''
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        toggleReplies?.()
                                    }}
                                >
                                    {repliesToggled
                                        ? `${t('hideReplies')} (${replies.length})`
                                        : `${t('viewReplies')} (${replies.length})`}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {showOptions ? (
                    <div
                        ref={optionsRef}
                        className={`comment__options${
                            optionsActive ? ' comment__options--active' : ''
                        }`}
                        style={{ zIndex: 100 }}
                    >
                        {canReport && (
                            <div
                                className='comment__options__option'
                                onClick={() => {
                                    handleCommentReport()
                                }}
                            >
                                <img
                                    src={AllIcons.post_report}
                                    alt={t('reportComment')}
                                />
                                {t('reportComment')}
                            </div>
                        )}

                        {canDelete && (
                            <div
                                className='comment__options__option'
                                onClick={() => {
                                    handleCommentDelete()
                                }}
                            >
                                <img
                                    src={AllIcons.post_delete}
                                    alt={t('deleteComment')}
                                />
                                {t('deleteComment')}
                            </div>
                        )}
                    </div>
                ) : (
                    ''
                )}
            </>
        )
    }
)

Comment.displayName = 'Comment'

export default Comment